import { useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useProfile } from "@/hooks/useProfile";
import { useCreateTask } from "@/hooks/useTaskAssignments";
import { PersonPicker } from "@/components/people/PersonPicker";
import type { Person } from "@/lib/people";
import { endOfDayMelbourneIso } from "@/lib/taskDueDate";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";

interface AssignTaskDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AssignTaskDialog({ open, onOpenChange }: AssignTaskDialogProps) {
  const { user } = useAuth();
  const { profile } = useProfile();
  const createTask = useCreateTask();

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [assignedTo, setAssignedTo] = useState("");
  const [assignee, setAssignee] = useState<Person | null>(null);
  const [priority, setPriority] = useState("normal");
  const [dueDate, setDueDate] = useState("");
  const [notes, setNotes] = useState("");

  const handleSubmit = () => {
    if (!title.trim() || !assignedTo || !user) return;

    createTask.mutate(
      {
        title: title.trim(),
        description: description.trim() || undefined,
        assigned_to: assignedTo,
        assigned_to_name: assignee?.name,
        assigned_by: user.id,
        assigned_by_name: profile?.full_name || profile?.email || undefined,
        priority,
        // End of the chosen day in Melbourne, not UTC midnight (taskDueDate.ts).
        due_date: dueDate ? endOfDayMelbourneIso(dueDate) : undefined,
        notes: notes.trim() || undefined,
      },
      {
        onSuccess: () => {
          resetForm();
          onOpenChange(false);
        },
      }
    );
  };

  const resetForm = () => {
    setTitle("");
    setDescription("");
    setAssignedTo("");
    setPriority("normal");
    setDueDate("");
    setNotes("");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Assign a Task</DialogTitle>
          <DialogDescription>Create and assign a task to a team member</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="task-title">Task Title *</Label>
            <Input id="task-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Review batch QC results" />
          </div>

          <div className="space-y-2">
            <Label htmlFor="task-assignee">Assign To *</Label>
            <PersonPicker
              id="task-assignee"
              value={assignedTo || null}
              onChange={(userId, person) => {
                setAssignedTo(userId);
                setAssignee(person);
              }}
              placeholder="Select a team member"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="task-priority">Priority</Label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="task-due">Due Date</Label>
              <Input id="task-due" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="task-description">Description</Label>
            <Textarea id="task-description" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Describe what needs to be done..." rows={2} />
          </div>

          <div className="space-y-2">
            <Label htmlFor="task-notes">Notes for Assignee</Label>
            <Textarea id="task-notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Any additional context or instructions..." rows={3} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={!title.trim() || !assignedTo || createTask.isPending}>
            {createTask.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Assign Task
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
